import { FACE_SIMILARITY_THRESHOLD, IDV_OVERALL_STATUS, MAX_RETRIES } from 'constants/idv';
import { parseIdKey } from 'helpers/ids';

import { MessageType } from 'components/FormComponents/FormMessage/FormMessage';

import i18n from '../i18n';

/**
 * Extract field codes from displayFields array recursively
 * @param {Array} fields - Array of field objects with code and subFields
 * @returns {Array} Array of field codes
 */
export const extractFieldCodes = fields => {
  const codes = [];
  if (!Array.isArray(fields)) return codes;
  fields.forEach(field => {
    if (field.code) {
      codes.push(field.code);
    }
    // Recursively check subFields
    if (field.subFields) {
      codes.push(...extractFieldCodes(field.subFields));
    }
  });
  return codes;
};

/**
 * Validate retry count and handle max retries scenario
 * @param {Object} upload - Upload object with metadata
 * @param {Object} callbacks - Object with callback functions for state updates
 * @param {Function} callbacks.setError - Function to set error message
 * @param {Function} callbacks.setVerificationError - Function to set verification error
 * @param {Function} callbacks.setErrorType - Function to set error type
 * @param {Function} callbacks.setStatus - Function to set status
 * @param {Function} callbacks.onChange - Function to call on change
 * @param {Function} callbacks.setIsFileRejected - Function to set rejected state
 * @param {boolean} useModel - Whether using model or ID
 * @param {string} status - Current verification status (optional, should be passed from handleVerificationResponse)
 */
export const validateRetryCount = (upload, callbacks, useModel = false, status = null) => {
  const currentAttempts = upload?.metadata?.attempts || 0;

  // Use status parameter (passed from handleVerificationResponse)
  const currentStatus = status;

  // Only show "accepted" message if at max retries AND status is failed/rejected/needs_review
  // Don't show it if status is verified (successful verification)
  if (
    currentAttempts >= MAX_RETRIES &&
    currentStatus &&
    (currentStatus === IDV_OVERALL_STATUS.FAILED ||
      currentStatus === IDV_OVERALL_STATUS.REJECTED ||
      currentStatus === IDV_OVERALL_STATUS.NEEDS_REVIEW)
  ) {
    const message =
      "We've accepted your document. Our support team will manually verify and contact you if needed.";
    callbacks.setError(message);
    if (callbacks.setVerificationError) {
      callbacks.setVerificationError(null);
    }
    callbacks.setErrorType(MessageType.Warning);
    callbacks.setStatus(IDV_OVERALL_STATUS.NEEDS_REVIEW || 'needs_review');
    callbacks.onChange(useModel ? upload : upload?.id || null);
    if (callbacks.setIsFileRejected) {
      callbacks.setIsFileRejected(false);
    }
  }
};

export const handleVerificationResponse = async (data, options, callbacks) => {
  const { getUpload, useModel, value, uploadId } = options;

  const {
    setStatus,
    setError,
    setErrorType,
    setVerificationError,
    setShowSelfieModal,
    onChange,
    setIsFileRejected,
    validateRetryCount: validateRetry,
  } = callbacks;

  // Get upload to check actual attempt count (source of truth)
  const upload = useModel ? value : await getUpload(uploadId);
  // Use metadata.attempts as source of truth (1-indexed: 1, 2, 3)
  const currentAttempts = upload?.metadata?.attempts ?? 0;

  setStatus(data?.status);

  // Handle skip_verification documents (status = not_applicable)
  // These are reference-only documents that don't need verification
  if (data?.status === IDV_OVERALL_STATUS.NOT_APPLICABLE) {
    if (setIsFileRejected) {
      setIsFileRejected(false);
    }
    if (onChange) {
      onChange(useModel ? value : uploadId);
    }
    return { success: true };
  }

  // Handle face similarity issues
  if (
    data?.face_similarity < FACE_SIMILARITY_THRESHOLD &&
    currentAttempts < MAX_RETRIES &&
    data?.face_similarity_status !== 'not_applicable' &&
    data?.has_photo === true &&
    callbacks.reTakeSelfie
  ) {
    if (setShowSelfieModal) {
      setShowSelfieModal(true);
    }
    setStatus(null);
    setError('Please upload your document again now');
    setErrorType(MessageType.Info);
    if (validateRetry) {
      validateRetry(upload, callbacks, useModel, data?.status);
    }
    return { success: false, needsRetry: true };
  } else if (
    data?.face_similarity < FACE_SIMILARITY_THRESHOLD &&
    currentAttempts < MAX_RETRIES &&
    data?.face_similarity_status !== 'not_applicable' &&
    data?.has_photo === true
  ) {
    // Not at max retries yet - show face mismatch error message
    setStatus(null);
    if (setVerificationError) {
      const idUploadTranslations = i18n.t('idUpload', {
        ns: 'id_document_types',
        returnObjects: true,
      });
      const faceMismatchMessage = idUploadTranslations?.reasonForFailure?.face_mismatch;
      setVerificationError(
        faceMismatchMessage || 'The face in the document does not match the face in the selfie.',
      );
    }
    if (validateRetry) {
      validateRetry(upload, callbacks, useModel, data?.status);
    }
    return { success: false, needsRetry: true };
  }

  // Handle failed verification
  const hasFailure =
    data?.status === 'failed' ||
    (Array.isArray(data?.reason_for_failure) && data.reason_for_failure.length > 0);
  if (hasFailure) {
    // Check if we're at max retries - if so, validateRetryCount will handle the messaging
    if (currentAttempts >= MAX_RETRIES) {
      if (validateRetry) {
        validateRetry(upload, callbacks, useModel, data?.status);
      }
      return { success: false };
    }

    // Not at max retries yet - show error message
    if (setVerificationError) {
      const failureKey =
        Array.isArray(data?.reason_for_failure) && data.reason_for_failure.length > 0
          ? data.reason_for_failure[0]
          : null;

      const idUploadTranslations = i18n.t('idUpload', {
        ns: 'id_document_types',
        returnObjects: true,
      });
      const translatedMessage =
        failureKey && idUploadTranslations?.reasonForFailure?.[failureKey]
          ? idUploadTranslations.reasonForFailure[failureKey]
          : null;

      setVerificationError(translatedMessage || 'Error verifying ID document. Please try again.');
    }
    if (validateRetry) {
      validateRetry(upload, callbacks, useModel, data?.status);
    }
    return { success: false };
  }

  // Verification successful
  if (setIsFileRejected) {
    setIsFileRejected(false);
  }
  // Call onChange on success
  if (onChange) {
    onChange(useModel ? value : uploadId);
  }
  return { success: true };
};

export const verifyIdDocument = async (options, callbacks) => {
  const {
    uploadId,
    metadata,
    listCode,
    expiryTolerance,
    verifyByToken,
    reTakeSelfie,
    getUpload,
    useModel,
    value,
    skipVerification = false,
  } = options;

  const {
    setVerifying,
    setStatus,
    setError,
    setErrorType,
    setVerificationError,
    setShowSelfieModal,
    onChange,
    setIsFileRejected,
  } = callbacks;

  const idCode = metadata?.id_type || null;
  if (!idCode) {
    return { success: false, error: 'No ID code provided' };
  }

  const { idType, countryCode } = parseIdKey(idCode);

  if (setVerifying) {
    setVerifying(true);
  }

  try {
    const data = await verifyByToken({
      listCode,
      uploadId,
      idCode,
      idType,
      idCountry: countryCode || null,
      expiryTolerance,
      skipVerification,
    });

    const result = await handleVerificationResponse(
      data,
      {
        verifyByToken,
        getUpload,
        metadata,
        listCode,
        expiryTolerance,
        reTakeSelfie,
        useModel,
        value,
        uploadId,
      },
      {
        ...callbacks,
        reTakeSelfie,
        validateRetryCount:
          callbacks.validateRetryCount ||
          ((upload, cb, useModel, status) => validateRetryCount(upload, cb, useModel, status)),
      },
    );

    return result;
  } catch (error) {
    console.error('Error verifying ID document:', error);
    if (setVerificationError) {
      setVerificationError(error?.message || 'Verification failed');
    }

    // Get upload for retry validation
    const upload = useModel ? value : await getUpload(uploadId);
    // In catch block, we don't have a status from the response, so pass null
    // validateRetryCount won't show the "accepted" message if status is null
    validateRetryCount(upload, callbacks, useModel, null);

    return { success: false, error: error?.message || 'Verification failed' };
  } finally {
    if (setVerifying) {
      setVerifying(false);
    }
  }
};

/**
 * Calculate border status based on upload status
 * @param {string} status - Upload status
 * @returns {string} Border status class
 */
export const getBorderStatus = status => {
  if (status === IDV_OVERALL_STATUS.FAILED || status === IDV_OVERALL_STATUS.REJECTED) {
    return 'u-border-error';
  }
  // verified, needs_review, and not_applicable all show success border
  if (
    status === IDV_OVERALL_STATUS.VERIFIED ||
    status === IDV_OVERALL_STATUS.NEEDS_REVIEW ||
    status === IDV_OVERALL_STATUS.NOT_APPLICABLE
  ) {
    return 'u-border-success';
  }
  return '';
};
